import { useMemo, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";

import localLawService from "api/localLawService";
import { ELocalLawAccess, ILegalActListItem } from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  act: ILegalActListItem;
  onClose: () => void;
  onSubmitted: () => void;
}

// Przekazanie projektu do akceptacji — pracownikowi nadrzędnemu, pracownikowi biura
// rady albo SOBIE SAMEMU. Odpowiednik „Przekaż" z listy projektów w Magistracie.
//
// Osoby z uprawnieniami biura rady są na górze listy i mają dopisek, bo to one
// prowadzą akt dalej (uchwalenie, publikacja, akty obowiązujące). Przekazanie do siebie
// ma sens w małym urzędzie, gdzie autor projektu jest zarazem osobą, która go zatwierdza.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const accessLabel = (access: number) => {
  if (access & ELocalLawAccess.BasicManagement) return "biuro rady — nadzór ogólny";

  const parts: string[] = [];
  if (access & ELocalLawAccess.ManageResolutions) parts.push("uchwały");
  if (access & ELocalLawAccess.ManageOrdinances) parts.push("zarządzenia");

  return parts.length ? `biuro rady — ${parts.join(", ")}` : "";
};

const SubmitModal = ({ act, onClose, onSubmitted }: Props) => {
  const [recipientId, setRecipientId] = useState("");
  const [comment, setComment] = useState("");

  const coworkersQuery = localLawService.useGetCoworkers();
  const { mutate: submit, isLoading } = localLawService.useSubmit();

  const coworkers = useMemo(() => {
    const list = coworkersQuery.data?.data ?? [];

    // Najpierw biuro rady (te osoby prowadzą akt dalej), potem reszta zespołu.
    return [...list].sort((a, b) => {
      const byAccess = (b.Access ? 1 : 0) - (a.Access ? 1 : 0);
      return byAccess !== 0 ? byAccess : a.Name.localeCompare(b.Name, "pl");
    });
  }, [coworkersQuery.data]);

  const selfSubmit = useMemo(
    () => coworkers.some((c) => c.IsMe && c.UserId === recipientId),
    [coworkers, recipientId],
  );

  const doSubmit = () => {
    if (!recipientId) return;

    submit(
      { id: act.Id, request: { RecipientUserId: recipientId, Comment: comment.trim() || null } },
      {
        onSuccess: onSubmitted,
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się przekazać projektu."),
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>Przekaż projekt do akceptacji</div>

        <div className={styles.modalBody}>
          {coworkersQuery.isLoading ? (
            <Loading />
          ) : (
            <>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Odbiorca</label>
                <select
                  className={styles.textInput}
                  value={recipientId}
                  onChange={(e) => setRecipientId(e.target.value)}
                >
                  <option value="">— wybierz osobę —</option>
                  {coworkers.map((c) => {
                    const notes = [c.IsMe ? "Ty" : null, accessLabel(c.Access)].filter(
                      Boolean,
                    );

                    return (
                      <option key={c.UserId} value={c.UserId}>
                        {c.Name}
                        {notes.length ? ` (${notes.join(", ")})` : ""}
                      </option>
                    );
                  })}
                </select>
                {coworkers.length === 1 && coworkers[0].IsMe && (
                  <span className={styles.hint}>
                    W Twojej przestrzeni roboczej nie ma innych osób — projekt możesz
                    przekazać do akceptacji sobie.
                  </span>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>Uwagi dla akceptującego</label>
                <textarea
                  className={styles.textArea}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              <span className={styles.hint}>
                {selfSubmit
                  ? "Projekt trafi do Twojej skrzynki „Akceptacja i Podpis” i będzie czekał na Twoją decyzję."
                  : "Projekt trafi do skrzynki „Akceptacja i Podpis” odbiorcy i zniknie z Twojej listy projektów do czasu decyzji."}
              </span>
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Przekaż"
            loading={isLoading}
            disabled={!recipientId}
            onClick={doSubmit}
          />
        </div>
      </div>
    </div>
  );
};

export default SubmitModal;
